import { ModalScreens } from 'components/Navigation/types';
import delay from 'delay';
import { isError, isNetworkError } from 'lib/isError';
import { apiGet, apiPost } from 'service/api';
import * as http from 'service/api/constants';
import {
  ALERT,
  ALERT_ERROR,
  HIDE_BLOCKING_LOADER,
  NAVIGATE_MODAL,
  NAVIGATE_MODAL_BACK,
  SHOW_BLOCKING_LOADER,
} from 'store/app/actions';
import { selectAssetIds } from 'store/assets/selectors';
import { sagaConfirm, sagaSelect } from 'store/sagaHelpers';
import {
  doCloudPropertiesOpen,
  doCloudPropertiesFilter,
  doCloudPropertiesSetData,
} from 'store/search/actions';
import { SearchOptions, SearchResults } from 'store/search/types';
import { put, select, takeEvery } from 'typed-redux-saga';

export const searchSideEffects = [
  takeEvery(doCloudPropertiesOpen, function* f(action) {
    try {
      yield* put(SHOW_BLOCKING_LOADER());
      const response = yield* apiGet<SearchOptions>(http.SEARCH_PREFLIGHT);
      yield* put(HIDE_BLOCKING_LOADER());
      // Must select an organisation first
      const organisationIds: string[] = [];
      if ((response.filter.organisations?.items?.length || 0) > 25) {
        yield* sagaSelect({
          modalSize: 'large',
          selectConfig: {
            title: 'Download Properties',
            subtitle: 'Select Organisations',
            optionNoun: 'Organisation',
            searchable: true,
            multiSelect: true,
            showQuickChoose: true,
            options: response.filter.organisations!.items.map((o) => ({
              name: o.name,
              internalValue: o.id,
            })),
          },
          onSelect: function* handleSelectOrganisations(values) {
            if (!values.length) {
              yield* put(
                ALERT({
                  text: 'You must choose at least one organisation',
                }),
              );
              return false;
            }
            organisationIds.push(...(values as string[]));
            return true;
          },
        });
        if (organisationIds.length === 0) {
          return;
        }
      }
      yield delay(1);
      // Open cloud properties modal
      yield* put(
        NAVIGATE_MODAL({
          screen: ModalScreens.CLOUDASSETS,
          params: {
            organisationIds,
            organisations: (response.filter.organisations?.items || []).map(
              (o) => ({
                id: o.id,
                name: o.name,
              }),
            ),
          },
        }),
      );
    } catch (error) {
      yield* put(HIDE_BLOCKING_LOADER());
      if (isError(error)) {
        yield* put(ALERT_ERROR({ action, error }));
      }
    }
  }),

  takeEvery(doCloudPropertiesFilter, function* f(action) {
    try {
      yield* put(SHOW_BLOCKING_LOADER());
      const response = yield* apiPost<
        SearchResults,
        {
          filter: {
            assessment_statuses: string[];
            exclude_assets: string[];
            organisations: string[];
            search: string;
          };
          sort: 'relevant:asc' | 'name:asc' | 'name:desc';
          limit: number;
          offset: number;
        }
      >(
        http.SEARCH,
        {
          filter: {
            assessment_statuses: [],
            exclude_assets: yield* select(selectAssetIds),
            organisations: action.payload.organisationIds,
            search: action.payload.search,
          },
          limit: 500,
          offset: 0,
          sort: action.payload.sortId,
        },
        {
          abortKey: 'cloudAssetsSearch',
        },
      );
      yield* put(HIDE_BLOCKING_LOADER());
      yield* put(doCloudPropertiesSetData(response));
    } catch (error) {
      yield* put(HIDE_BLOCKING_LOADER());
      if (isNetworkError(error) && !error.isCancelled) {
        const tryAgain = yield* sagaConfirm({
          title: 'Network Error',
          text: `${error.message}\n\nTry again or close the download window?`,
          cancelText: 'Close Download',
          confirmText: 'Try Again',
        });
        if (tryAgain === true) {
          yield* put(doCloudPropertiesFilter(action.payload));
        } else {
          yield* put(NAVIGATE_MODAL_BACK());
        }
      }
    }
  }),
];
